import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Phone, ChevronRight, Info } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const convenios = [
  "Amil",
  "Assefaz",
  "Bradesco Saúde",
  "BRF",
  "Caixa",
  "Capesesp",
  "Casf",
  "Conab",
  "E-vida (Eletronorte)",
  "Fusex",
  "Geap",
  "Medial",
  "Medservice",
  "Mondial",
  "Pax Nacional Prever",
  "Pax Voibs",
  "Postal Saúde",
  "Pame",
  "São Francisco Saúde",
  "Sam Bemat",
  "Sempec",
  "Sest Senat",
  "Sulamérica",
  "Servsaúde",
  "Unimed",
  "Unimed Intercâmbio",
  "White Martins",
];

const Convenios = () => {
  const { settings } = useSettings();
  const wa = settings?.contact?.whatsapp || "5565996516708";

  return (
    <div data-testid="convenios-page">
      <section className="bg-gradient-to-br from-primary via-primary to-primary/85 text-primary-foreground pt-28 md:pt-32 pb-12 md:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-30" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-accent blob" />
        <div className="container-pad relative">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-8 h-px bg-accent" />
            <span className="text-[11px] tracking-[0.3em] text-accent font-semibold uppercase">CONVÊNIOS ACEITOS</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-light leading-tight mb-3 text-balance max-w-3xl">Atendimento via plano de saúde</h1>
          <p className="max-w-2xl text-primary-foreground/75 text-sm md:text-base">Atendemos os principais convênios médicos no Hospital Otorrino, em Cuiabá-MT. Confira abaixo a lista atualizada.</p>
        </div>
      </section>

      <section className="container-pad py-14 md:py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* List */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent to-emerald-600 text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
              <div>
                <div className="font-display text-xl md:text-2xl text-primary font-semibold">{convenios.length} convênios aceitos</div>
                <div className="text-xs text-muted-foreground">Hospital Otorrino — Cuiabá-MT</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {convenios.map((nome, i) => (
                <motion.div
                  key={nome}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 9) * 0.04 }}
                  className="group bg-white border border-border rounded-2xl px-4 py-3.5 flex items-center gap-3 hover:border-accent hover:shadow-md transition"
                  data-testid={`convenio-${i}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-secondary to-secondary/40 text-primary group-hover:from-accent group-hover:to-accent/80 group-hover:text-white transition flex items-center justify-center font-display font-semibold text-xs shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display text-sm md:text-base text-primary group-hover:text-accent transition font-semibold truncate">{nome}</div>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 p-5 bg-secondary/40 rounded-2xl border-l-4 border-accent flex gap-4">
              <Info className="w-5 h-5 text-accent shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/80 leading-relaxed">
                A lista de convênios pode sofrer alterações. Antes de realizar sua consulta, confirme com a recepção a cobertura do seu plano para a especialidade desejada e os procedimentos necessários.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <aside>
            <div className="sticky top-28 space-y-4">
              <div className="bg-white border border-border rounded-3xl p-5 ring-medical">
                <div className="text-[11px] uppercase tracking-widest text-accent font-semibold mb-2">Pré-agendamento</div>
                <h3 className="font-display text-lg text-primary font-semibold mb-2">Fale com a secretária</h3>
                <p className="text-sm text-muted-foreground mb-4">Verifique a disponibilidade do seu convênio e agende sua consulta.</p>
                <a
                  href={`https://wa.me/${wa}?text=${encodeURIComponent("Olá, gostaria de saber se meu convênio é aceito e agendar uma consulta.")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-accent text-accent-foreground text-sm font-medium hover:-translate-y-0.5 transition"
                  data-testid="convenios-whatsapp"
                >
                  <Phone className="w-4 h-4" /> WhatsApp
                </a>
                <Link
                  to="/contato"
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-primary/20 text-primary text-sm font-medium hover:bg-primary hover:text-primary-foreground transition"
                >
                  Formulário de contato <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="bg-gradient-to-br from-primary to-primary/85 text-primary-foreground rounded-3xl p-5 relative overflow-hidden">
                <div className="absolute inset-0 pattern-dots opacity-30" />
                <div className="relative">
                  <div className="text-[11px] uppercase tracking-widest text-accent font-semibold mb-2">Unidade</div>
                  <h3 className="font-display text-lg font-semibold mb-2">Hospital Otorrino</h3>
                  <p className="text-xs text-primary-foreground/80 leading-relaxed">Rua Tenente Eulálio Guerra, 51 — Araés, Cuiabá-MT, CEP 78005-510</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Convenios;
